import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { ContactUrl } from "../../../../shared/setup/appUrl";
import { DocModel } from "../state/state";
import { EnvironmentService } from "../environment/environment.service";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { getApp } from "../app";
import { LoginDetails } from "../../../../shared/models/login-details";
import { REDIRECT_REASON } from "../routing/routing.service";
import { StringUtils } from "../../../../shared/utils/string.utils";
import { TOKEN } from "./token";

const DB_ENTRA_AUTO_LOGIN_STARTED = "dbEntraAutoLoginStarted";

@Component({
    selector: "app-auth",
    templateUrl: "./auth.component.html",
    styleUrls: ["./auth.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AuthComponent implements OnInit {
  @ViewChild("resetPasswordModal", { static: true })
  resetPasswordModal: any;
  details: LoginDetails = {};

  loginForm: FormGroup;

  app = getApp((app) => {
    this.app = app;
  });

  model: DocModel = {};
  passwordError: boolean = false;

  showEntraLogin: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private environment: EnvironmentService
  ) {
    this.loginForm = this.formBuilder.group({
      email: ["", [Validators.required, Validators.email]],
      password: ["", Validators.required]
    });
  }

  async ngOnInit() {
    // Workaround for Obsolescence-Community that should have the language in English
    if (this.app.environment.isOC) {
      this.app.state.next({ language: "en" });
    }

    // Update favicon
    this.app.environment.setFavicon(document.getElementById("favicon")!);

    // Update title
    this.app.environment.setTitle();

    const token = localStorage.getItem(TOKEN);
    const redirectReason = sessionStorage.getItem(REDIRECT_REASON);

    if (token !== "" && token !== null) {
      if (redirectReason !== "" && redirectReason !== null) {
        this.app.state.errorText = this.app.text.auth.MFASessionExpired;
        this.app.state.hasError = true;
        sessionStorage.removeItem(REDIRECT_REASON);
        localStorage.removeItem(TOKEN);

        setTimeout(() => {
          this.app.state.next({ hasError: false });
        }, 10000);
      } else {
        const authenticated = await this.checkAuth();

        if (authenticated) {
          return;
        }
      }
    }

    if (!this.app.environment.isPortal && !this.app.environment.isOC) {
      this.showEntraLogin = true;
    }

    if (
      this.app.environment.isDB &&
      sessionStorage.getItem(DB_ENTRA_AUTO_LOGIN_STARTED) !== "true"
    ) {
      await this.entraLogin();
    }
  }

  get fLogin() {
    return this.loginForm.controls;
  }

  async checkAuth(): Promise<boolean> {
    const authenticated = await this.app.auth.checkAuth();

    if (authenticated && this.app.customer !== null) {
      this.app.routing.navigate(this.app.customer, "home");
    }

    return authenticated;
  }

  async attemptLogin(): Promise<void> {
    this.app.auth.loginBtnDisabled = true;
    this.app.hasError = false;
    this.passwordError = false;
    this.app.loginError = false;
    this.app.errorText = "";
    this.details.user = this.fLogin.email.value.toLowerCase();
    this.details.password = this.fLogin.password.value;

    try {
      if (StringUtils.isNullOrEmpty(this.details.user)) {
        this.app.hasError = true;
        this.app.errorLoginName = true;
        this.app.errorText = this.app.text.auth.loginName;
        return;
      } else if (StringUtils.isNullOrEmpty(this.details.password)) {
        this.app.hasError = true;
        this.app.errorLoginPassword = true;
        this.app.errorText = this.app.text.auth.loginPassword;
        return;
      }
      let result = await this.app.auth.login(this.details);
      if (this.app.state.passwordChangeRequired) {
        this.openModal();
      }
    } catch (err) {
      this.app.auth.loginBtnDisabled = false;
      let errorMessage = (err as any).message;
      if (errorMessage == "user inactive") {
        this.app.hasError = true;
        this.app.loginError = true;
        this.app.errorText = this.app.text.auth.inactiveUser;
      }
      if (errorMessage == "user deactivated") {
        this.app.hasError = true;
        this.app.loginError = true;
        this.app.errorText = this.app.text.auth.userDeactivated;
      }
      if (errorMessage === "not logged in") {
        this.passwordError = true;
      }
      if (errorMessage === "not_authorized") {
        this.app.hasError = true;
        this.app.loginError = true;
        this.app.errorText = this.app.text.auth.notAuthorized;
      }
    }
  }

  async entraLogin() {
    let returnUrl = `${this.app.environment.baseUrl}/external-redirect`;

    if (this.app.environment.isDB) {
      sessionStorage.setItem(DB_ENTRA_AUTO_LOGIN_STARTED, "true");
    }

    const result = await this.app.auth.entraLogin(returnUrl);

    window.location.href = result.returnUrl;
  }

  getContactFormURL() {
    if (this.environment.isPortal) {
      const langSuffix = this.app.language === "de" ? "" : "/en";
      return `${ContactUrl.portalContactUrl}${langSuffix}/#contact`;
    }

    if (this.environment.isOC && this.app.language === "en") {
      return `${ContactUrl.ocContactUrl}/#contact`;
    }

    const langSuffix = this.app.language === "de" ? "/de" : "";
    return `${ContactUrl.baseContactUrl}${langSuffix}/#contact`;
  }

  openModal() {
    this.resetPasswordModal.open({ backdrop: "static", keyboard: false });
  }
}
